<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('scale.scales.title')"
      :show-add="canEdit"
      :show-filter="false"
      @columns="openColumnChooser"
      @export="exportGrid"
      @add="handleAddClick"
    />

    <DxDataGrid
      :data-source="scalesDataSurce"
      :remote-operations="gridRemoteOperations"
      :column-min-width="150"
      :showBorders="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :searchPanel="{
        visible: false,
        width: 240,
        placeholder: $t('common.search'),
      }"
      :ref="gridRefKey"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      @init-new-row="onInitNewRow"
    >
      <DxColumn dataField="name" :caption="$t('common.name')" :filter-operations="['contains']">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn dataField="scale_id" :caption="$t('scale.scales.scale_id')" :filter-operations="['contains']">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        dataField="scale_house_uuid"
        :caption="$t('scale.scales.scale_house')"
        :lookup="{
          dataSource: getScaleHousesLookup,
          valueExpr: 'uuid',
          displayExpr: 'name',
        }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>

      <DxColumn dataField="scale_type" :caption="$t('scale.scales.scale_type')" :filter-operations="['contains']"> </DxColumn>
      <DxColumn dataField="installation_code" :caption="$t('scale.scales.installation_code')" :filter-operations="['contains']"> </DxColumn>
      <DxColumn dataField="accuracy_class" :caption="$t('scale.scales.accuracy_class')" :filter-operations="['contains']"> </DxColumn>

      <DxColumn
        dataField="weighing_type"
        :caption="$t('common.weighing_type')"
        dataType="string"
        :lookup="{
          dataSource: ['STATIC', 'DYNAMIC'], // The selectable values
          valueExpr: null, // Use the raw value directly
          displayExpr: null, // Use the raw value directly
        }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        data-field="bound_rate"
        :caption="$t('scale.scales.transmission_rate')"
        :lookup="{ dataSource: [2400, 4800, 9600, 19200, 38400, 57600, 115200] }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn data-field="parity" :caption="$t('scale.scales.parity')" :lookup="{ dataSource: ['None', 'Odd', 'Even', 'Mark', 'Space'] }">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn data-field="data_bits" :caption="$t('scale.scales.data_bits')" :lookup="{ dataSource: [5, 6, 7, 8] }">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        data-field="stop_bits"
        :caption="$t('scale.scales.stop_bits')"
        :lookup="{ dataSource: ['None', 'One', 'Two', 'OnePointFive'] }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn dataField="com_port" :caption="$t('scale.scales.com_port')" dataType="number" :editorOptions="{ showSpinButtons: false }">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        dataField="minimum_measurable_weight_kg"
        :caption="$t('scale.scales.minimum_measurable_weight_kg')"
        dataType="number"
        :editorOptions="{ min: 0, max: 1000, showSpinButtons: false }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        dataField="is_default"
        :caption="$t('scale.scales.is_default')"
        dataType="number"
        :customize-text="customizeIsDefaultText"
        :form-item="{
          editorType: 'dxCheckBox',
        }"
        :visible="true"
        :width="120"
      />
      <template #passwordEditCell="{ data }">
        <DxTextBox
          :value="data.value"
          :mode="showPasswordInGrid ? 'text' : 'password'"
          :buttons="passwordToggleButtons"
          @value-changed="(e) => data.setValue(e.value)"
        />
      </template>
      <DxColumn
        dataField="password"
        :caption="$t('common.password')"
        :allowFiltering="false"
        :allowSorting="false"
        :visible="false"
        :show-in-column-chooser="false"
        :form-item="{ visible: true, visibleIndex: 16 }"
        :customize-text="maskPasswordText"
        edit-cell-template="passwordEditCell"
      />
      <DxColumn
        dataField="updated_at"
        :caption="$t('common.updated_at')"
        dataType="date"
        :visible="true"
        :allowFiltering="true"
        :allowSorting="true"
        :allowEditing="false"
        :filter-operations="['contains']"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
        :editorOptions="{ displayFormat: getDxGridDefaultDateFormat + ' HH:mm', disabled: true }"
      >
      </DxColumn>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
        :editable="false"
        :editorOptions="{ disabled: true }"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu :grid-ref="$refs[gridRefKey]" row-key="uuid" :row="data.data" :can-edit="canEdit" :can-delete="canDelete" />
      </template>
      <DxEditing mode="form" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('scale.scales.title_edit'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :save-row-changes="$t('common.save')"
          :cancel-row-changes="$t('common.close')"
          :addRow="$t('common.add_new')"
          :editRow="$t('common.edit')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxScrolling mode="standard" column-rendering-mode="standard" show-scrollbar="always" />
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxColumnChooser :enabled="false" mode="select" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxButton,
  DxPopup,
  DxToolbar,
  DxItem,
  DxRequiredRule,
  DxColumnChooser,
  DxScrolling,
} from "devextreme-vue/data-grid";
import DxTagBox from "devextreme-vue/tag-box";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import DxTextBox from "devextreme-vue/text-box";
import DxValidator, { DxValidationRule } from "devextreme-vue/validator";
import notify from "devextreme/ui/notify";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

export default {
  name: "Scales",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxToolbar,
    DxItem,
    Forbidden,
    DxTagBox,
    DxRequiredRule,
    DxColumnChooser,
    DxScrolling,
    DxTextBox,
    DxValidator,
    DxValidationRule,
    GridPanelHeader,
    DxActionMenu,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid scales-grid",
      },
      gridRefKey: "scalesGrid",
      scalesDataSurce: new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: this.load,
          insert: this.insert,
          update: this.update,
          remove: this.remove,
        }),
      }),
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 50, 100],
      showPasswordInGrid: false,
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      scales: "scale/scales",
      scaleHouses: "scale/scaleHouses",
    }),
    config() {
      return {
        permission: permissions.SCALES,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    passwordToggleButtons() {
      return [
        {
          name: "togglePassword",
          location: "after",
          options: {
            icon: this.showPasswordInGrid ? "fa fa-eye-slash" : "fa fa-eye",
            stylingMode: "text",
            onClick: () => {
              this.showPasswordInGrid = !this.showPasswordInGrid;
            },
          },
        },
      ];
    },
    dataGridInstance() {
      return this.$refs[this.gridRefKey].instance;
    },
  },
  methods: {
    ...mapActions({
      getScaleHouses: "scale/getScaleHouses",
      getScales: "scale/getScales",
      addScale: "scale/createScale",
      updateScale: "scale/updateScale",
      deleteScale: "scale/deleteScale",
    }),
    async load(loadOptions) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      let payload = {
        limit: 999999,
      };

      try {
        await this.getScales(payload).then((response) => {
          dataSource.data = response.data;
          dataSource.totalCount = 1;
        });

        return dataSource;
      } catch (error) {
        console.error("Error fetching scales:", error);
        throw error;
      }
    },
    async insert(obj) {
      try {
        const normalizedPayload = this.prepareScalePayload(obj);

        if (this.normalizeIsDefault(normalizedPayload.is_default) && normalizedPayload.scale_house_uuid) {
          await this.clearScaleHouseDefault(normalizedPayload.scale_house_uuid);
        }

        await this.addScale(normalizedPayload).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error adding scale :", error);
        throw error;
      }
    },
    async update(key, obj) {
      try {
        const currentScale = this.scales.find((scale) => scale.uuid === key) || {};
        const nextScaleHouseUuid = obj?.scale_house_uuid || currentScale?.scale_house_uuid;
        const nextIsDefault = this.getNextIsDefaultValue(obj, currentScale);

        if (nextIsDefault && nextScaleHouseUuid) {
          await this.clearScaleHouseDefault(nextScaleHouseUuid, key);
        }

        const normalizedPayload = this.prepareScalePayload({
          ...obj,
          uuid: key,
          is_default: nextIsDefault,
        });

        await this.updateScale(normalizedPayload).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error updating scale :", error);
        throw error;
      }
    },
    async remove(key) {
      try {
        await this.deleteScale({ uuid: key }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error deleting scale :", error);
        throw error;
      }
    },
    onEditingStart(e) {
      this.showPasswordInGrid = false;
    },
    onInitNewRow(e) {},
    normalizeIsDefault(value) {
      return value === true || value === 1 || value === "1";
    },
    getNextIsDefaultValue(obj, currentScale) {
      if (Object.prototype.hasOwnProperty.call(obj || {}, "is_default")) {
        return this.normalizeIsDefault(obj.is_default);
      }

      return this.normalizeIsDefault(currentScale?.is_default);
    },
    prepareScalePayload(payload) {
      return {
        ...payload,
        is_default: this.normalizeIsDefault(payload?.is_default) ? 1 : 0,
      };
    },
    async clearScaleHouseDefault(scaleHouseUuid, excludeUuid = null) {
      if (!scaleHouseUuid) {
        return;
      }

      const response = await this.getScales({
        limit: 999999,
        scale_house_uuid: scaleHouseUuid,
      });

      const scaleRows = Array.isArray(response?.data) ? response.data : [];
      const defaultRows = scaleRows.filter(
        (scale) => this.normalizeIsDefault(scale?.is_default) && scale?.uuid !== excludeUuid,
      );

      for (const scale of defaultRows) {
        await this.updateScale({
          uuid: scale.uuid,
          is_default: 0,
        });
      }
    },
    getIsDefaultColumnText(rowData) {
      return this.normalizeIsDefault(rowData?.is_default) ? this.$t("common.yes") : this.$t("common.no");
    },
    customizeIsDefaultText(cellInfo) {
      return this.normalizeIsDefault(cellInfo.value) ? this.$t("common.yes") : this.$t("common.no");
    },
    maskPasswordText(cellInfo) {
      const value = cellInfo?.value;

      if (!value) {
        return "";
      }

      return "*".repeat(String(value).length);
    },
    openColumnChooser() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.showColumnChooser();
      }
    },
    exportGrid() {
      const instance = this.dataGridInstance;
      if (!instance) return;

      this.onExporting({
        component: instance,
        format: "xlsx",
        fileName: "Scales_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss"),
      });
    },
    handleAddClick() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.addRow();
      }
    },
    onExporting(e) {
      e.fileName = "Scales_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Scales");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    getScaleHousesLookup() {
      if (!this.scaleHouses || (this.scaleHouses.length ?? 0) === 0) {
        return [];
      }
      return this.scaleHouses.map((scaleHouse) => ({
        uuid: scaleHouse.uuid,
        name: scaleHouse.name,
      }));
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status == "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
  },
  mounted() {
    this.getScaleHouses();
  },
};
</script>
